import React, { useState, useEffect } from 'react';
import { ContentEditorProps } from './types';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import BrandResources from '@/components/brand/BrandResources';
import { useUnsavedChangesPrompt } from '@/hooks/useUnsavedChangesPrompt';
import { ToastSave } from '@/components/ui/toast-save';

/**
 * Editor for blog post content type and landing page content type
 */

// --- TYPES ---
type BonusMetadata = {
  slug: string;
  tags: string[];
  excerpt: string;
  keywords: string[];
  schema_markup: string;
  call_to_action: string;
  target_audience: string;
  meta_description: string;
  author: string;
  reading_time?: string;
};

type Section = { heading: string; content: string };
type BlogPostFormState = {
  title: string;
  image_url: string;
  ideal_media_type: 'image' | 'video' | '';
  ideal_media_description: string;
  bonus_metadata: BonusMetadata;
  content_sections: Section[];
};

export const BlogPostEditor: React.FC<ContentEditorProps> = ({ 
  content, 
  onSave, 
  onCancel,
  onUnsavedChanges
}) => {
  // --- FORM STATE ---
  const [formState, setFormState] = useState<BlogPostFormState>(() => {
    const { 
      title, 
      image_url, 
      ideal_media_type, 
      ideal_media_description, 
      bonus_metadata = {}, 
      content_sections = [] 
    } = content.content_json || {};
    return {
      title: title || '',
      image_url: image_url || '',
      ideal_media_type: ideal_media_type || '',
      ideal_media_description: ideal_media_description || '',
      bonus_metadata: {
        slug: bonus_metadata.slug || '',
        tags: bonus_metadata.tags || [],
        excerpt: bonus_metadata.excerpt || '',
        keywords: bonus_metadata.keywords || [],
        schema_markup: bonus_metadata.schema_markup || '',
        call_to_action: bonus_metadata.call_to_action || '',
        target_audience: bonus_metadata.target_audience || '',
        meta_description: bonus_metadata.meta_description || '',
        author: bonus_metadata.author || '',
        reading_time: bonus_metadata.reading_time || '',
      },
      content_sections: Array.isArray(content_sections) && content_sections.length ? content_sections : [ { heading: '', content: '' } ],
    };
  });
  const [initialFormState, setInitialFormState] = useState(formState);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [saveState, setSaveState] = useState<'initial' | 'loading' | 'success'>('initial');
  useUnsavedChangesPrompt(unsavedChanges);

  const [imagePickerOpen, setImagePickerOpen] = useState(false);

  // --- HANDLERS ---
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  // For bonus_metadata fields
  const handleBonusMetadataChange = (field: keyof BonusMetadata, value: string | string[]) => {
    setFormState(prev => ({
      ...prev,
      bonus_metadata: {
        ...prev.bonus_metadata,
        [field]: value,
      },
    }));
  };

  // Tag input for tags/keywords
  const handleTagInput = (field: 'tags' | 'keywords', value: string[]) => {
    handleBonusMetadataChange(field, value);
  };

  // --- CONTENT SECTIONS HANDLERS ---
  const handleSectionChange = (index: number, field: 'heading' | 'content', value: string) => {
    setFormState(prev => {
      const sections = [...prev.content_sections];
      sections[index] = { ...sections[index], [field]: value };
      return { ...prev, content_sections: sections };
    });
  };

  const addSection = () => {
    setFormState(prev => ({
      ...prev,
      content_sections: [...prev.content_sections, { heading: '', content: '' }],
    }));
  };

  const removeSection = (index: number) => {
    setFormState(prev => {
      const sections = [...prev.content_sections];
      sections.splice(index, 1);
      return { ...prev, content_sections: sections };
    });
  };

  const moveSection = (index: number, direction: 'up' | 'down') => {
    setFormState(prev => {
      const sections = [...prev.content_sections];
      if (direction === 'up' && index > 0) {
        [sections[index - 1], sections[index]] = [sections[index], sections[index - 1]];
      } else if (direction === 'down' && index < sections.length - 1) {
        [sections[index], sections[index + 1]] = [sections[index + 1], sections[index]];
      }
      return { ...prev, content_sections: sections };
    });
  };

  const handleOpenImagePicker = () => {
    setImagePickerOpen(true);
  };

  const handleRemoveImage = () => {
    setFormState(prev => ({
      ...prev,
      image_url: ''
    }));
  };

  const handleSelectImage = (resource: any) => {
    if (resource && resource.url_to_inject) {
      setFormState(prev => ({
        ...prev,
        image_url: resource.url_to_inject
      }));
    }
    setImagePickerOpen(false);
  };

  // Update unsavedChanges state whenever formState changes
  useEffect(() => {
    const hasChanges = JSON.stringify(formState) !== JSON.stringify(initialFormState);
    setUnsavedChanges(hasChanges);
    // Notify parent about unsaved changes
    if (onUnsavedChanges) {
      onUnsavedChanges(hasChanges);
    }
  }, [formState, initialFormState, onUnsavedChanges]);

  // --- FORM SUBMIT ---
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveState('loading');
    try {
      // Create the updated content object
      const updatedContent: Partial<typeof content> = {
        content_json: {
          ...content.content_json,
          title: formState.title,
          image_url: formState.image_url,
          ideal_media_type: formState.ideal_media_type,
          ideal_media_description: formState.ideal_media_description,
          bonus_metadata: formState.bonus_metadata,
          content_sections: formState.content_sections,
        },
        name: formState.title,
      };
      await onSave(updatedContent, true);
      setInitialFormState(formState);
      setSaveState('success');
      setTimeout(() => setSaveState('initial'), 2000);
    } catch (error) {
      setSaveState('initial');
      console.error('Error saving blog post:', error);
    }
  };

  // For ToastSave (no event param)
  const handleSave = () => {
    // Create a fake event to call handleSubmit
    // (or refactor handleSubmit to not require event)
    handleSubmit({ preventDefault: () => {} } as React.FormEvent);
  };

  const handleReset = () => {
    setFormState(initialFormState);
    setSaveState('initial');
  };

  // --- TAG INPUT COMPONENT ---
  const TagInput = ({ value, onChange, placeholder }: { value: string[]; onChange: (v: string[]) => void; placeholder?: string }) => {
    const [input, setInput] = useState('');
    const addTag = () => {
      if (input.trim() && !value.includes(input.trim())) {
        onChange([...value, input.trim()]);
        setInput('');
      }
    };
    const removeTag = (idx: number) => {
      onChange(value.filter((_, i) => i !== idx));
    };
    return (
      <div className="flex flex-wrap gap-2">
        {value.map((tag, idx) => (
          <span key={tag} className="bg-gray-200 dark:bg-gray-700 rounded px-2 py-1 flex items-center text-sm">
            {tag}
            <button type="button" className="ml-1 text-gray-500 hover:text-red-500" onClick={() => removeTag(idx)}>&times;</button>
          </span>
        ))}
        <input
          className="border rounded px-2 py-1 text-sm focus:outline-none"
          value={input}
          placeholder={placeholder}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              addTag();
            }
          }}
          onBlur={addTag}
        />
      </div>
    );
  };

  // --- BONUS METADATA EDITOR ---
  const renderBonusMetadata = () => {
    return (
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>SEO & Metadata</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Slug */}
          <div>
            <Label htmlFor="slug">Slug</Label>
            <Input
              id="slug"
              name="slug"
              value={formState.bonus_metadata.slug}
              onChange={e => handleBonusMetadataChange('slug', e.target.value)}
              placeholder="URL slug (e.g. my-awesome-post)"
            />
          </div>
          {/* Tags */}
          <div>
            <Label>Tags</Label>
            <TagInput
              value={formState.bonus_metadata.tags ?? []}
              onChange={tags => handleTagInput('tags', tags)}
              placeholder="Add tag and press Enter"
            />
          </div>
          {/* Keywords */}
          <div>
            <Label>Keywords</Label>
            <TagInput
              value={formState.bonus_metadata.keywords ?? []}
              onChange={keywords => handleTagInput('keywords', keywords)}
              placeholder="Add keyword and press Enter"
            />
          </div>
          {/* Excerpt */}
          <div>
            <Label htmlFor="excerpt">Excerpt</Label>
            <Textarea
              id="excerpt"
              name="excerpt"
              value={formState.bonus_metadata.excerpt}
              onChange={e => handleBonusMetadataChange('excerpt', e.target.value)}
              placeholder="Short summary for SEO/social preview"
              className="h-20"
            />
          </div>
          {/* Meta Description */}
          <div>
            <Label htmlFor="meta_description">Meta Description</Label>
            <Textarea
              id="meta_description"
              name="meta_description"
              value={formState.bonus_metadata.meta_description}
              onChange={e => handleBonusMetadataChange('meta_description', e.target.value)}
              placeholder="Meta description for SEO"
              className="h-20"
            />
          </div>
          {/* Call to Action */}
          <div>
            <Label htmlFor="call_to_action">Call to Action</Label>
            <Input
              id="call_to_action"
              name="call_to_action"
              value={formState.bonus_metadata.call_to_action}
              onChange={e => handleBonusMetadataChange('call_to_action', e.target.value)}
              placeholder="e.g. Plan your next adventure!"
            />
          </div>
          {/* Target Audience */}
          <div>
            <Label htmlFor="target_audience">Target Audience</Label>
            <Input
              id="target_audience"
              name="target_audience"
              value={formState.bonus_metadata.target_audience}
              onChange={e => handleBonusMetadataChange('target_audience', e.target.value)}
              placeholder="e.g. Adventure travelers, space fans"
            />
          </div>
          {/* Author */}
          <div>
            <Label htmlFor="author">Author</Label>
            <Input
              id="author"
              name="author"
              value={formState.bonus_metadata.author}
              onChange={e => handleBonusMetadataChange('author', e.target.value)}
              placeholder="e.g. John Smith, Travel Writer"
            />
          </div>
          {/* Reading Time */}
          <div>
            <Label htmlFor="reading_time">Reading Time</Label>
            <Input
              id="reading_time"
              name="reading_time"
              value={formState.bonus_metadata.reading_time}
              onChange={e => handleBonusMetadataChange('reading_time', e.target.value)}
              placeholder="e.g. 5 min read"
            />
          </div>
          {/* Schema Markup */}
          <div>
            <Label htmlFor="schema_markup">Schema Markup (JSON-LD)</Label>
            <Textarea
              id="schema_markup"
              name="schema_markup"
              value={formState.bonus_metadata.schema_markup}
              onChange={e => handleBonusMetadataChange('schema_markup', e.target.value)}
              placeholder="Paste JSON-LD here"
              className="h-32 font-mono"
            />
            {/* Collapsible preview */}
            <Accordion type="single" collapsible className="mt-2">
              <AccordionItem value="schema-preview">
                <AccordionTrigger>Preview Schema JSON</AccordionTrigger>
                <AccordionContent>
                  <pre className="bg-gray-100 dark:bg-gray-800 rounded p-2 text-xs overflow-x-auto">
                    {(() => {
                      try {
                        return JSON.stringify(JSON.parse(formState.bonus_metadata.schema_markup), null, 2);
                      } catch {
                        return 'Invalid JSON';
                      }
                    })()}
                  </pre>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </CardContent>
      </Card>
    );
  };

  // --- CONTENT SECTIONS EDITOR ---
  const renderContentSections = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-lg">Content Sections</h3>
        <Button type="button" variant="outline" onClick={addSection}>
          Add Section
        </Button>
      </div>
      {formState.content_sections.map((section, idx) => (
        <Card key={idx} className="relative">
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex-1">
              <Input
                className="font-semibold text-base mb-2"
                placeholder={`Section Heading (e.g. Introduction, Part ${idx + 1})`}
                value={section.heading}
                onChange={e => handleSectionChange(idx, 'heading', e.target.value)}
              />
            </div>
            <div className="flex gap-1 ml-2">
              <Button type="button" size="icon" variant="ghost" onClick={() => moveSection(idx, 'up')} disabled={idx === 0} title="Move Up">
                ↑
              </Button>
              <Button type="button" size="icon" variant="ghost" onClick={() => moveSection(idx, 'down')} disabled={idx === formState.content_sections.length - 1} title="Move Down">
                ↓
              </Button>
              <Button type="button" size="icon" variant="destructive" onClick={() => removeSection(idx)} title="Remove Section">
                ×
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Image picker and preview - only show in first section */}
            {idx === 0 && (
              <div className="mb-4">
                <Label className="text-sm font-medium mb-2 block">Blog Post Image</Label>
                {formState.image_url ? (
                  <div className="relative mb-2">
                    <img src={formState.image_url} alt="Blog Post" className="max-h-48 rounded border w-full object-contain" />
                    <div className="absolute top-2 right-2 flex gap-2">
                      <Button type="button" size="icon" variant="destructive" onClick={() => handleRemoveImage()} title="Remove Image">×</Button>
                      <Button type="button" size="icon" variant="outline" onClick={() => handleOpenImagePicker()} title="Change Image">🖼️</Button>
                    </div>
                  </div>
                ) : (
                  <Button type="button" variant="outline" onClick={() => handleOpenImagePicker()}>
                    Add Image
                  </Button>
                )}
              </div>
            )}
            <Textarea
              className="font-mono h-32"
              placeholder="Section content (markdown or HTML allowed)"
              value={section.content}
              onChange={e => handleSectionChange(idx, 'content', e.target.value)}
            />
          </CardContent>
        </Card>
      ))}
      {/* Image Picker Dialog */}
      <Dialog open={imagePickerOpen} onOpenChange={setImagePickerOpen}>
        <DialogContent className="max-w-5xl h-[80vh] flex flex-col">
          <DialogTitle>Select Image</DialogTitle>
          <div className="flex-grow overflow-hidden">
            <BrandResources
              mode="picker"
              onResourceSelect={handleSelectImage}
              onCancel={() => setImagePickerOpen(false)}
              allowedTypes={['image']}
              brandId={content.brand_id}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );

  // --- MAIN RENDER ---
  return (
    <div className="w-full h-full overflow-auto bg-white dark:bg-gray-900 p-6">
      {/* ToastSave bar for unsaved changes */}
      {unsavedChanges && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50">
          <ToastSave
            state={saveState}
            onSave={handleSave}
            onReset={handleReset}
            loadingText="Saving changes..."
            successText="All changes saved"
            initialText="You have unsaved changes"
            resetText="Cancel"
            saveText="Save"
          />
        </div>
      )}
      <form onSubmit={handleSubmit}>
        <Card className="w-full max-w-4xl mx-auto">
          <CardHeader>
            <CardTitle>Edit Blog Post</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                name="title"
                value={formState.title}
                onChange={handleChange}
                placeholder="Enter blog post title"
                required
              />
            </div>
            {/* Bonus Metadata Section */}
            {renderBonusMetadata()}
            {/* Content Sections Editor */}
            {renderContentSections()}
          </CardContent>
          <CardFooter className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit">
              Save Changes
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
};

export default BlogPostEditor; 